import { extend } from 'lodash-es';

import { TokenKeeper } from '../../../../../../../../frontend/libs/shared/src/lib/services/tokenKeeper';

/** ApplicationPermissions Service */
export interface ApplicationPermission {
  action: string;
  allowed: boolean;
}

/** Add / Update User (via My Account) services */
export interface NewUser {
  firstName: string;
  middleInitial: string;
  lastName: string;
  jobTitle: string;
  emailAddress: string;
  mobilePhone: string;
  businessPhone: string;
  fax: string;
  permissions: string[];
  notificationSubscriptions?: NotificationSubscription[];
}

export interface User {
  firstName: string;
  middleInitial: string;
  lastName: string;
  jobTitle: string;
  emailAddress: string;
  mobilePhone: string;
  businessPhone: string;
  fax: string;
  id: number;
  status: string;
  organization: OrganizationSummary;
  permissions: string[];
  notificationSubscriptions: NotificationSubscription[];
  categoryNotificationSubscriptions: CategoryNotificationSubscription[];
  primaryAddress?: Address;
  secondaryAddress?: Address;
  lastActivityTime?: number;
}

export interface UserProfile {
  lastName: string;
  firstName: string;
  emailAddress: string;
  middleInitial: string;
  mobilePhone: string;
  jobTitle: string;
  businessPhone: string;
  fax: string;
  notificationSubscriptions?: NotificationSubscription[];
  permissions?: string[];
  primaryAddress?: Address;
  secondaryAddress?: Address;
}

/** Account interfaces for current logged in service */

// Every type of user account extends this
// Same order as the account service provides, for simplicity
interface BaseAccount<TOrganization> {
  emailAddress: string;
  environment: any;
  fullName: string;
  groupId: number;
  hasAptitudeAgreement: boolean;
  isBuyer: boolean;
  isGroup: boolean;
  isLeader: boolean;
  isPartner: boolean; // deprecated - needs to be removed from backend
  isRoleAptitudeAdmin: boolean;
  isRoleBuyerAdmin: boolean;
  isRoleSellerAdmin: boolean;
  isSeller: boolean;
  isSystem: boolean;
  numberOfOrgsUserBelongsTo: number;
  organization: TOrganization;
  permissions: any;
  userId: number;
  idToken: string;
}

type PartialAccount<TOrganization> = Partial<BaseAccount<TOrganization>>;

// Every type of buyer account extends this
interface BaseBuyerAccount extends BaseAccount<BuyerOrganization> {
  isBuyer: true;
  isSeller: false;
  // organization: BuyerOrganization;
}

/**
 * Our unit tests don't want to specify every null value
 */
export type PartialBuyerAccount = PartialAccount<PartialBuyerOrganization>;

// Every type of seller account extends this
interface BaseSellerAccount extends BaseAccount<SellerOrganization> {
  isBuyer: false;
  isSeller: true;
}

/**
 * Our unit tests don't want to specify every null value
 */
export type PartialSellerAccount = PartialAccount<PartialSellerOrganization>;

export interface Leader extends BaseBuyerAccount {
  isLeader: true;
}

// Partner and Solo have identical types
export interface Partner extends BaseBuyerAccount {
  isLeader: false;
}

// Buyers are either Leaders or Partners/Solo
type BuyerAccount = Leader | Partner;

export interface SellerAccount extends BaseSellerAccount {
  isBuyer: false;
  isSeller: true;
}

// Users are either Buyers or Sellers
export type UserAccount = BuyerAccount | SellerAccount;

export function isLeader(user: UserAccount): user is Leader {
  return user.isBuyer && user.isLeader;
}

export function isPartner(user: UserAccount): user is Partner {
  return user.isBuyer && !user.isLeader;
}

export function isSeller(user: UserAccount): user is SellerAccount {
  return user.isSeller;
}

interface OrganizationSummary {
  id: number;
  name: string;
}

interface Organization {
  id: number;
  name: string;
  gln: any;
  city: string;
  state: string;
  zip: any;
  address1: any;
  address2: any;
  organizationType: 'Group' | 'HCO' | 'Supplier';
  isSystem: boolean;
  isBuyer: boolean;
  isGroup: boolean;
  isSeller: boolean;
  hasAptitudeAgreement: boolean;
}

export interface BuyerOrganization extends Organization {
  organizationType: 'Group' | 'HCO';
  isBuyer: true;
  isSeller: false;
}
/**
 * Our unit tests don't want to specify every null value
 */
type PartialBuyerOrganization = Partial<BuyerOrganization>;

export interface SellerOrganization extends Organization {
  organizationType: 'Supplier';
  isBuyer: false;
  isSeller: true;
}

/**
 * Our unit tests don't want to specify every null value
 */
type PartialSellerOrganization = Partial<SellerOrganization>;

interface Address {
  id: number;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

interface NotificationSubscription {
  subscribed: boolean;
  appEvent: {
    displayName: string;
    name: string;
  };
}

interface CategoryNotificationSubscription {
  subscribed: boolean;
  category: {
    id: number;
    name: string;
  };
}

export default class UsersService {
  baseUrl = '/aptitude/api/users/';

  constructor(private $http) {
    'ngInject';
  }

  account(userId: number | string, cache = true): ng.IPromise<UserAccount> {
    return this.$http
      .get(`${this.baseUrl}${userId}/account`, { cache })
      .then((user) => {
        if (userId === 'me' && user.idToken) {
          TokenKeeper.getPlatformSingleton().setToken(user.idToken);
        }

        return user;
      });
  }

  currentUser(cache = true): ng.IPromise<UserAccount> {
    return this.account('me', cache).then((response) => {
      return response;
    });
  }

  getUserById(userId: number): ng.IPromise<UserProfile> {
    return this.$http.get(`${this.baseUrl}${userId}`);
  }

  updateUser(
    userId: number,
    data: UserProfile,
    config: any,
  ): ng.IPromise<UserProfile> {
    const payload: any = {
      firstName: data.firstName,
      middleInitial: data.middleInitial,
      lastName: data.lastName,
      jobTitle: data.jobTitle,
      mobilePhone: data.mobilePhone,
      businessPhone: data.businessPhone,
      fax: data.fax,
      emailAddress: data.emailAddress,
    };

    // Both primary and secondary address are optional an only added/updated if passed
    // If updating the user's existing address, pass the address id. Not necessary if adding for the first time
    if (data.primaryAddress) {
      payload.primaryAddress = {
        address1: data.primaryAddress.address1,
        address2: data.primaryAddress.address2,
        city: data.primaryAddress.city,
        state: data.primaryAddress.state,
        zip: data.primaryAddress.zip,
      };

      if (data.primaryAddress.id) {
        payload.primaryAddress.id = data.primaryAddress.id;
      }
    }

    if (data.secondaryAddress) {
      payload.secondaryAddress = {
        address1: data.secondaryAddress.address1,
        address2: data.secondaryAddress.address2,
        city: data.secondaryAddress.city,
        state: data.secondaryAddress.state,
        zip: data.secondaryAddress.zip,
      };

      if (data.secondaryAddress.id) {
        payload.secondaryAddress.id = data.secondaryAddress.id;
      }
    }

    return this.$http.put(`${this.baseUrl}${userId}`, payload, config);
  }

  addUser(user: NewUser, config): ng.IPromise<User> {
    return this.$http.post(this.baseUrl, user, config);
  }

  removeAddress(userId, addressId): ng.IPromise<any> {
    return this.$http.delete(this.baseUrl + userId + '/addresses/' + addressId);
  }

  profile(userId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}${userId}/profile`);
  }

  organizations(userId: number | string): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}${userId}/organizations`);
  }

  switchAccount(userId: number | string, orgId: number): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}${userId}/account`, {
      organization: {
        id: orgId,
      },
    });
  }

  updateProfile(userId: number): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}${userId}/profile`, null);
  }

  updateStatus(userId: number, status): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}${userId}/status`, {
      accountStatus: status,
    });
  }

  applicationPermissions(
    userId: number,
    context,
    resourceId?: number,
    supplementalId?: number,
    cache = false,
  ): ng.IPromise<ApplicationPermission[]> {
    const config = extend(
      {
        params: {
          context: context,
          resourceId: resourceId,
          supplementalId: supplementalId,
        },
      },
      { cache },
    );

    return this.$http.get(
      `${this.baseUrl}${userId}/applicationpermissions`,
      config,
    );
  }

  /**
   * Load application permissions for the purchase planning page
   *
   * This is a developer-friendly wrapper for the generic application permissions call above
   *
   * @param {number} userId         Logged in user id
   * @param {number} purchasePlanId Purchase plan id for the selected plan
   */
  loadPurchasePlanningPermissions(
    userId: number,
    purchasePlanId: number,
  ): ng.IPromise<any> {
    return this.applicationPermissions(
      userId,
      'PURCHASE_PLANNING',
      purchasePlanId,
    );
  }

  /**
   * Load application permissions for the seller response / offer pages
   *
   * @param  {number}           userId   Logged in user id
   * @param  {number}           rfpId    The current rfp
   * @param  {number}           sellerId The current seller
   * @return {ng.IPromise<any>}
   */
  getResponsePermissions(
    userId: number,
    rfpId: number,
    sellerId: number,
  ): ng.IPromise<any> {
    return this.applicationPermissions(
      userId,
      'SELLER_RFP_RESPONSE',
      rfpId,
      sellerId,
    );
  }

  getBuyerImpactPermissions(
    entityId,
    supplementalId,
  ): ng.IPromise<ApplicationPermission[]> {
    const context = 'GROUP_MASTER_IMPACT';

    const config = {
      intercept: true,
      params: {
        context,
        supplementalId,
        entityId,
      },
    };

    return this.$http.get(
      '/aptitude/ApplicationPermissionsServiceController/getApplicationPermissions',
      config,
    );
  }

  addPermission(userId: number, permissionName): ng.IPromise<any> {
    return this.$http.post(`${this.baseUrl}${userId}/permissions`, {
      permission: permissionName,
    });
  }

  removePermission(userId: number, permissionName): ng.IPromise<any> {
    return this.$http.delete(
      `${this.baseUrl}${userId}/permissions/${permissionName}`,
    );
  }

  updateUserNotifications(
    userId: number,
    notificationName: string,
    isSubscribed: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}${userId}/notificationsubscriptions/${notificationName}`,
      {
        subscribed: isSubscribed,
        scope: 'USER',
      },
    );
  }

  updateCategoryNotificationSubscriptions(
    userId: number,
    categoryId: number,
    isSubscribed: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(
      this.baseUrl +
        userId +
        '/categorynotificationsubscriptions/' +
        categoryId,
      {
        subscribed: isSubscribed,
        scope: 'USER',
      },
    );
  }

  /**
   * Get Users
   *
   * By default, gets the users in the org
   * To get the users in the group, pass in the group id
   */
  users(groupId?: number): ng.IPromise<any> {
    let url = this.baseUrl;

    if (groupId) {
      url += '?groupId=' + groupId;
    }

    return this.$http.get(url);
  }
}
